import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Mail, 
  Users, 
  Send, 
  Clock, 
  AlertCircle,
  CheckCircle,
  Eye,
  History,
  RefreshCw,
  Calendar
} from 'lucide-react';

const AdminReengagementPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [daysInactive, setDaysInactive] = useState(30);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [previewRes, campaignsRes] = await Promise.all([
        api.get(`/api/admin/reengagement-preview?days_inactive=${daysInactive}`),
        api.get('/api/admin/email-campaigns')
      ]);
      setPreview(previewRes.data);
      setCampaigns(campaignsRes.data.campaigns || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/reengagement-preview?days_inactive=${daysInactive}`);
      setPreview(res.data);
      toast.success('Preview atualizado');
    } catch (error) {
      toast.error('Erro ao carregar preview');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCampaign = async () => {
    if (!preview || preview.total_inactive_users === 0) {
      toast.error('Nenhum usuário inativo encontrado');
      return;
    }

    const confirmed = window.confirm(
      `Tem certeza que deseja enviar emails de reengajamento para ${preview.total_inactive_users} usuários inativos há ${daysInactive}+ dias?`
    );

    if (!confirmed) return;

    setSending(true);
    try {
      const res = await api.post(`/api/admin/reengagement-campaign?days_inactive=${daysInactive}`);
      toast.success(`Campanha concluída: ${res.data.emails_sent} emails enviados`);
      loadData(); // Reload to get updated campaign history
    } catch (error) {
      toast.error('Erro ao enviar campanha');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !preview) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002FA7]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      {/* Header */}
      <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin')}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Reengajamento</h1>
            <p className="text-sm text-gray-500">Campanha de emails para usuários inativos</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Column - Campaign Settings */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Campaign Card */}
            <div className="bg-white border border-[#E4E4E7] rounded-lg overflow-hidden">
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 rounded-full p-2">
                    <Mail className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">Campanha de Reengajamento</h2>
                    <p className="text-sm text-white/80">Reative usuários que não acessam há tempo</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                {/* Days selector */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Usuários inativos há:
                  </label>
                  <div className="flex items-center gap-4">
                    <select
                      value={daysInactive}
                      onChange={(e) => setDaysInactive(Number(e.target.value))}
                      className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      data-testid="days-inactive-select"
                    >
                      <option value={15}>15 dias</option>
                      <option value={30}>30 dias</option>
                      <option value={60}>60 dias</option>
                      <option value={90}>90 dias</option>
                    </select>
                    <Button
                      variant="outline"
                      onClick={handlePreview}
                      disabled={loading}
                      data-testid="preview-btn"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Atualizar Preview
                    </Button>
                  </div>
                </div>

                {/* Stats */}
                {preview && (
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-amber-50 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-amber-600" />
                        <span className="text-sm text-amber-700">Usuários Inativos</span>
                      </div>
                      <p className="text-2xl font-bold text-amber-900 mt-1">
                        {preview.total_inactive_users}
                      </p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-blue-600" />
                        <span className="text-sm text-blue-700">Período</span>
                      </div>
                      <p className="text-2xl font-bold text-blue-900 mt-1">
                        {preview.days_threshold}+ dias
                      </p>
                    </div>
                  </div>
                )}

                {/* Preview list */}
                {preview && preview.preview && preview.preview.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                      Preview (primeiros 10 usuários):
                    </h3>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left font-medium text-gray-600">Usuário</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-600">Dias Inativo</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-600">Chamadas</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {preview.preview.map((user, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-2">
                                <div className="font-medium text-gray-900">{user.name || 'Sem nome'}</div>
                                <div className="text-xs text-gray-500">{user.email}</div>
                              </td>
                              <td className="px-4 py-2">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  user.days_inactive > 60 ? 'bg-red-100 text-red-700' :
                                  user.days_inactive > 30 ? 'bg-amber-100 text-amber-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {user.days_inactive} dias
                                </span>
                              </td>
                              <td className="px-4 py-2">
                                <span className="text-gray-600">{user.matching_calls} relevantes</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Send button */}
                <Button
                  onClick={handleSendCampaign}
                  disabled={sending || !preview || preview.total_inactive_users === 0}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="send-campaign-btn"
                >
                  {sending ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Enviar Campanha ({preview?.total_inactive_users || 0} emails)
                    </>
                  )}
                </Button>

                {/* Info box */}
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <div className="flex gap-2">
                    <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Como funciona:</p>
                      <ul className="list-disc list-inside space-y-1 text-blue-700">
                        <li>Usuários que já receberam email nos últimos 14 dias não serão contatados</li>
                        <li>O email mostra chamadas relevantes para cada usuário</li>
                        <li>Máximo de 100 emails por execução</li>
                        <li>Usuários podem desativar nas configurações do perfil</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Campaign History */}
          <div className="space-y-6">
            <div className="bg-white border border-[#E4E4E7] rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <History className="h-5 w-5 text-gray-600" />
                  <h3 className="font-semibold text-gray-900">Histórico de Campanhas</h3>
                </div>
              </div>
              
              <div className="p-4">
                {campaigns.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">
                    Nenhuma campanha enviada ainda
                  </p>
                ) : (
                  <div className="space-y-3">
                    {campaigns.map((campaign, idx) => (
                      <div key={idx} className="border border-gray-100 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-500">
                            <Calendar className="h-3 w-3 inline mr-1" />
                            {formatDate(campaign.created_at)}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            campaign.emails_sent > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {campaign.type === 'reengagement' ? 'Reengajamento' : campaign.type}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <div className="flex items-center gap-1">
                            <Users className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-600">{campaign.users_found} encontrados</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            <span className="text-gray-600">{campaign.emails_sent} enviados</span>
                          </div>
                        </div>
                        {campaign.errors_count > 0 && (
                          <div className="mt-1 text-xs text-red-600">
                            {campaign.errors_count} erro(s)
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminReengagementPage;
